import { sva } from '@allenai/varnish-panda-runtime/css';
import type { RecipeVariantProps } from '@allenai/varnish-panda-runtime/types';

/**
 *  The <input type="range"> HTML element can be used to build a slider, however it is very
 *  difficult to style cross browser. Slider helps achieve accessible sliders that can be styled as
 *  needed.
 *
 *  See: https://react-spectrum.adobe.com/react-aria/Slider.html
 */

const sliderRecipe = sva({
    slots: [
        'root',
        'label',
        'output',
        'control',
        'track',
        'indicator',
        'thumb',
        'description',
        'error',
    ],
    base: {
        root: {
            display: 'grid',
            gridTemplateColumns: '[1fr auto]',
        },
        // This is the <label> for the slider, Label component has default styles
        label: {},
        // this is the value (for the slider)
        output: {
            color: 'text',
        },
        control: {
            gridColumn: '[1 / -1]',
            display: 'flex',
            alignItems: 'center',
            cursor: 'pointer',
            _disabled: {
                colorPalette: 'elements.disabled',
                cursor: 'not-allowed',
            },
            _componentReadOnly: {
                cursor: 'default',
                pointerEvents: 'none',
            },
        },
        track: {
            position: 'relative',
            borderRadius: 'full',

            // all variants have the same track color, so we don't use `colorPalette`
            backgroundColor: 'background.opacity-10',
        },
        indicator: {
            borderRadius: 'full',
            backgroundColor: 'colorPalette.stroke',

            _groupHoverNotDisabledNotSelected: {
                backgroundColor: 'colorPalette.hovered.stroke',
            },
            _groupDragging: {
                backgroundColor: 'colorPalette.hovered.stroke',
            },
        },
        thumb: {
            boxSizing: 'border-box',
            borderRadius: 'full',
            width: 'var(--slider-control-size)',
            aspectRatio: '1',

            backgroundColor: 'colorPalette.fill',
            _hover: {
                backgroundColor: 'colorPalette.hovered.fill',
            },
            // dragging has the same styling as hover
            _dragging: {
                backgroundColor: 'colorPalette.hovered.fill',
            },
        },
        description: {},
        error: {},
    },
    variants: {
        color: {
            default: {
                control: {
                    colorPalette: 'elements.default',
                },
            },
            primary: {
                control: {
                    colorPalette: 'elements.primary',
                },
            },
            secondary: {
                control: {
                    colorPalette: 'elements.secondary',
                },
            },
            tertiary: {
                control: {
                    colorPalette: 'elements.tertiary',
                },
            },
        },
        size: {
            small: {
                root: {
                    '--slider-control-size': '[0.75rem]',
                    '--slider-track-size': '[0.25rem]',
                },
            },
            medium: {
                root: {
                    '--slider-control-size': '[0.875rem]',
                    '--slider-track-size': '[0.25rem]',
                },
            },
            large: {
                root: {
                    '--slider-control-size': '[1.25rem]',
                    '--slider-track-size': '[0.375rem]',
                },
            },
        },
        orientation: {
            horizontal: {
                root: {
                    width: '[100%]',
                },
                output: {},
                control: {
                    height: 'var(--slider-control-size)',
                    width: '[100%]',
                },
                track: {
                    height: 'var(--slider-track-size)',
                    width: '[100%]',
                },
                indicator: {
                    top: '[0]',
                    height: '[100%]',
                },
                thumb: {
                    top: '[50%]',
                },
            },
            vertical: {
                root: {
                    height: '[100%]',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                },
                output: {
                    marginBottom: '[calc(var(--slider-control-size) / 2)]',
                },
                control: {
                    width: 'var(--slider-control-size)',
                    height: '[100%]',
                    justifyContent: 'center',
                },
                track: {
                    width: 'var(--slider-track-size)',
                    height: '[100%]',
                },
                indicator: {
                    left: '[0]',
                    width: '[100%]',
                },
                thumb: {
                    left: '[50%]',
                },
            },
        },
    },
    defaultVariants: {
        color: 'default',
        size: 'medium',
        orientation: 'horizontal',
    },
});

type SliderRecipeProps = RecipeVariantProps<typeof sliderRecipe>;

export { sliderRecipe, sliderRecipe as default };
export type { SliderRecipeProps };
